// #ux-redesign-start: Drawer primitive (Paso 2)
// Panel lateral con backdrop. En mobile pasa a full-screen.
// Pensado para reemplazar modales de detalle pesados (AssignDoctorModal,
// KitDetailModal, DerivationHistoryModal) por un solo drawer con tabs.
//
// PORTABILIDAD: trasplantable a Web-Patients.
//
// USO:
//   <Drawer isOpen={open} onClose={close} title="Studie · Anna Holz" side="right" size="md">
//     {content}
//   </Drawer>
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useTranslate } from 'react-translate';

const Drawer = ({
  isOpen,
  onClose,
  title,
  side = 'right',
  size = 'md',
  closeOnBackdrop = true,
  closeOnEsc = true,
  className = '',
  children,
  footer,
  backdropTestId,
}) => {
  const ta = useTranslate('a11y');
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEsc, onClose]);

  useEffect(() => {
    // Lock body scroll cuando el drawer está abierto
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const classes = [
    'ds-drawer',
    `ds-drawer--${side}`,
    `ds-drawer--size-${size}`,
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <div className={classes} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Dialog'}>
      <div
        className="ds-drawer__backdrop"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
        testId={backdropTestId}
      />
      <div className="ds-drawer__panel">
        {title && (
          <header className="ds-drawer__header">
            <h2 className="ds-drawer__title">{title}</h2>
            <button
              type="button"
              className="ds-drawer__close"
              onClick={onClose}
              aria-label={ta('close')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </header>
        )}
        <div className="ds-drawer__body">
          {children}
        </div>
        {footer && (
          <footer className="ds-drawer__footer">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );

  // Portal al body para que escape de stacking contexts raros
  return ReactDOM.createPortal(content, document.body);
};

Drawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  side: PropTypes.oneOf(['right', 'left']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  closeOnBackdrop: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
  backdropTestId: PropTypes.string,
};

export default Drawer;
// #ux-redesign-end
